/**
 * Upload article cover image to S3 via studio presigned URL (same flow as video thumbnails).
 */

import {getStudioAuthHeaders} from '#/lib/api/studio'

const AWS_S3_BUCKET =
  (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_AWS_S3_BUCKET) ||
  'shxrevappblob'
const AWS_S3_REGION =
  (typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_AWS_S3_REGION) ||
  'us-east-2'
const DEFAULT_PRESIGNED_ENDPOINT =
  'https://shxre.social/api/s3/presigned-url'
const PRESIGNED_URL_ENDPOINT =
  (typeof process !== 'undefined' &&
    process.env?.EXPO_PUBLIC_S3_PRESIGNED_URL_ENDPOINT) ||
  DEFAULT_PRESIGNED_ENDPOINT

function getPresignedEndpoint(): string {
  if (
    !PRESIGNED_URL_ENDPOINT ||
    !PRESIGNED_URL_ENDPOINT.includes('presigned-url')
  )
    return DEFAULT_PRESIGNED_ENDPOINT
  try {
    const u = new URL(PRESIGNED_URL_ENDPOINT)
    if (u.hostname === 'share.social') return DEFAULT_PRESIGNED_ENDPOINT
  } catch {
    return DEFAULT_PRESIGNED_ENDPOINT
  }
  return PRESIGNED_URL_ENDPOINT
}

const presignedEndpoint = getPresignedEndpoint()

function getMakePublicEndpoint(): string {
  return presignedEndpoint.replace(
    '/api/s3/presigned-url',
    '/api/s3/make-public',
  )
}

async function makeS3ObjectPublic(key: string): Promise<void> {
  const res = await fetch(getMakePublicEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getStudioAuthHeaders(),
    },
    body: JSON.stringify({key}),
  })
  if (!res.ok) console.warn('Failed to set S3 object public:', key, res.status)
}

export type ArticleCoverInput =
  | {type: 'file'; file: File}
  | {type: 'uri'; uri: string; fileSize?: number; mimeType?: string}
  | null

/**
 * Uploads an image to S3 under studio/articles/covers/ and returns the public URL.
 * Use this URL as cover_image_path when creating/updating an article.
 */
export async function uploadArticleCoverToS3(
  input: ArticleCoverInput,
): Promise<string> {
  if (!input) throw new Error('No image')
  const baseName =
    input.type === 'file'
      ? input.file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      : 'cover.jpg'
  const key = `studio/articles/covers/${Date.now()}-${baseName}`
  const contentType =
    input.type === 'file'
      ? input.file.type || 'image/jpeg'
      : input.mimeType || 'image/jpeg'
  let fileSize: number
  let body: Blob | File
  if (input.type === 'file') {
    fileSize = input.file.size
    body = input.file
  } else {
    const blob = await (await fetch(input.uri)).blob()
    fileSize = input.fileSize ?? blob.size
    body = blob
  }
  const res = await fetch(presignedEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getStudioAuthHeaders(),
    },
    body: JSON.stringify({key, contentType, fileSize}),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error((err as {error?: string}).error || res.statusText)
  }
  const {url} = (await res.json()) as {url: string}
  await fetch(url, {
    method: 'PUT',
    headers: {'Content-Type': contentType},
    body,
  })
  await makeS3ObjectPublic(key)
  return `https://${AWS_S3_BUCKET}.s3.${AWS_S3_REGION}.amazonaws.com/${key}`
}
